import { dashboardPaint, dashboardCss } from '../utils/dashboardTheme';
// ✅ FIXED: AdminContext.jsx - Performance Optimized Version
// src/contexts/AdminContext.jsx

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { UserModel } from '../db/models/userModel';
import { PerformanceModel } from '../db/models/performanceModel';
import { AssessmentModel } from '../db/models/assessmentModel';
import { AssessmentSettingsModel } from '../db/models/assessmentSettingsModel';
import { api } from '../api/client';
import {
  assessmentIsOwnedBy,
  isEnabledFlag,
  isUserOwnedAssessment,
  sameAssessment,
  settingIsAssigned,
} from '../utils/assessmentIdentity';

/* ──────────────────────────────────────────────────────────────────
   Helpers to normalise DB rows → plain JS objects
─────────────────────────────────────────────────────────────────── */
const normalizeUser = (user) => {
  // ✅ CRITICAL: Consistent boolean conversion
  const restrictDays = isEnabledFlag(user.restrict_days !== undefined
    ? user.restrict_days 
    : user.restrictDays !== undefined 
    ? user.restrictDays 
    : true); // Default to enabled
    
  const restrictTime = isEnabledFlag(user.restrict_time !== undefined
    ? user.restrict_time 
    : user.restrictTime !== undefined 
    ? user.restrictTime 
    : false); // Default to disabled
    
  const isEnabled = isEnabledFlag(user.is_enabled !== undefined
    ? user.is_enabled 
    : user.isEnabled !== undefined 
    ? user.isEnabled 
    : true); // Default to enabled
  
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    pinCode: user.pin_code || user.pinCode,
    accessCode: user.access_code || user.accessCode,
    role: user.role,
    dateAssigned: user.date_assigned || user.dateAssigned,
    validityDays: user.validity_days || user.validityDays || 300,
    isEnabled: isEnabled,
    restrictTime: restrictTime,
    accessStartTime: user.access_start_time || user.accessStartTime || '00:00',
    accessEndTime: user.access_end_time || user.accessEndTime || '23:59',
    restrictDays: restrictDays,
    // ✅ CRITICAL FIX: Handle empty/null daysAllowed properly and respect restrictDays setting
    daysAllowed: (() => {
      const days = user.days_allowed || user.daysAllowed;
       
      // ✅ CRITICAL: If restrictDays is disabled, always return empty array
      if (!restrictDays) {
        return [];
      }
      
      // ✅ CRITICAL FIX: Handle empty string as intentional "block all days"
      if (days === '') {
        return []; // Empty array = block all days
      }
      
      // If days is null or undefined, use default weekdays (only for truly new users)
      if (days === null || days === undefined) {
        return ['1', '2', '3', '4', '5'];
      }
      
      // If it's already an array, return it
      if (Array.isArray(days)) {
        return days.map(d => String(d)); // Ensure all are strings
      }
      
      // If it's a string, split it
      if (typeof days === 'string') {
        const result = days.split(',').map(d => d.trim()).filter(d => d !== '');
        return result;
      }
      
      // Fallback 
      return restrictDays ? ['1', '2', '3', '4', '5'] : [];
    })(),
    maxSubUsers:
      user.max_subusers !== undefined
        ? user.max_subusers
        : user.maxSubUsers !== undefined
        ? user.maxSubUsers
        : 5,
    parentUserId:
      user.parent_user_id !== undefined && user.parent_user_id !== null
        ? Number(user.parent_user_id)
        : user.parentUserId !== undefined && user.parentUserId !== null
        ? Number(user.parentUserId)
        : null,
  };
};

// ✅ FIXED: Updated normalizePerformance to include timer_seconds
// Replace the normalizePerformance function in AdminContext.jsx (around line 65)

const normalizePerformance = (perf) => {
  const isComplete =
    perf.isComplete === 1 ||
    perf.isComplete === true ||
    (typeof perf.isComplete === 'string' &&
      perf.isComplete.toLowerCase() === 'true');
  
  // ✅ CRITICAL FIX: Add timer_seconds normalization with proper type conversion
  const timerSeconds = (() => {
    // Check both snake_case and camelCase variants
    const timer = perf.timer_seconds !== undefined ? perf.timer_seconds : perf.timerSeconds;
    
    // Convert to number and ensure it's valid
    const parsed = parseInt(timer) || 0;
    return Math.max(0, parsed); // Ensure non-negative
  })();
  const assessmentId = String(
    perf.assessment_id ?? perf.assessmentId ?? perf.assessment_type ?? ''
  ).trim();
  const assessmentName =
    perf.assessment_name_snapshot ||
    perf.assessmentName ||
    perf.assessment_name ||
    assessmentId;
  const continueCode = String(perf.continue_code ?? perf.continueCode ?? '').trim();
  const lifecycleStatus = String(perf.lifecycle_status ?? perf.lifecycleStatus ?? '').trim().toLowerCase();
  const submittedAt = perf.submitted_at ?? perf.submittedAt ?? null;
  const startedAt = perf.started_at ?? perf.startedAt ?? null;
  const lastActivityAt = perf.last_activity_at ?? perf.lastActivityAt ?? null;
  const pausedAt = perf.paused_at ?? perf.pausedAt ?? null;
  const resumedAt = perf.resumed_at ?? perf.resumedAt ?? null;
  const archivedAt = perf.archived_at ?? perf.archivedAt ?? null;
  const completionReason = perf.completion_reason ?? perf.completionReason ?? null;
  
  return {
    id: perf.id,
    learnerAccountId: perf.learner_account_id ?? perf.learnerAccountId ?? null,
    studentName: perf.student_name || perf.studentName,
    assessmentId,
    assessmentName,
    assessment_file_snapshot: perf.assessment_file_snapshot || perf.assessmentFileSnapshot || '',
    assessmentFileSnapshot: perf.assessment_file_snapshot || perf.assessmentFileSnapshot || '',
    assessment_definition_checksum: perf.assessment_definition_checksum || perf.assessmentDefinitionChecksum || '',
    assessmentDefinitionChecksum: perf.assessment_definition_checksum || perf.assessmentDefinitionChecksum || '',
    assessmentKind: perf.assessment_kind || perf.assessmentKind,
    gradeLevel: perf.grade_level || perf.gradeLevel,
    actualGradeLevel: perf.actual_grade_level || perf.actualGradeLevel,
    gradePerformance: perf.grade_performance || perf.gradePerformance,
    codeUsed: perf.code_used || perf.codeUsed,
    testDate: perf.test_date || perf.testDate,
    testTime: perf.test_time || perf.testTime,
    isComplete,
    continue_code: continueCode,
    continueCode,
    lifecycle_status: lifecycleStatus,
    lifecycleStatus,
    submitted_at: submittedAt,
    submittedAt,
    started_at: startedAt,
    startedAt,
    last_activity_at: lastActivityAt,
    lastActivityAt,
    paused_at: pausedAt,
    pausedAt,
    resumed_at: resumedAt,
    resumedAt,
    archived_at: archivedAt,
    archivedAt,
    completion_reason: completionReason,
    completionReason,
    record_revision: Number(perf.record_revision ?? perf.recordRevision ?? 1) || 1,
    recordRevision: Number(perf.record_revision ?? perf.recordRevision ?? 1) || 1,
    tts_enabled: isEnabledFlag(perf.tts_enabled ?? perf.ttsEnabled) ? 1 : 0,
    assessment_type: assessmentId,
    assessment_pool: perf.assessment_pool || perf.assessmentPool || 'NA',
    assessmentPool: perf.assessment_pool || perf.assessmentPool || 'NA',
    
    // ✅ CRITICAL FIX: Add timer fields in both formats for compatibility
    timer_seconds: timerSeconds,
    timerSeconds: timerSeconds, // camelCase version for frontend
  };
};

/* ──────────────────────────────────────────────────────────────────
   Context creation
─────────────────────────────────────────────────────────────────── */
const AdminContext = createContext(null);

/* ──────────────────────────────────────────────────────────────────
   Provider component
─────────────────────────────────────────────────────────────────── */
export function AdminProvider({ children }) {
  /* ------------ state ------------ */
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [performances, setPerformances] = useState([]);
  // The authenticated server catalog is authoritative. Starting with the
  // former hard-coded defaults created "ghost" rows when an assessment had
  // been deleted or renamed in the database.
  const [availableAssessments, setAvailableAssessments] = useState([]);
  const [assessmentSettings, setAssessmentSettings] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isCreatingUser, setIsCreatingUser] = useState(false);

  /* ------------------------------------------------------------------
     ✅ NEW: Enhanced notifier for better teacher settings support
  ------------------------------------------------------------------*/
  const [settingsChangeNotifiers] = useState({
    listeners: [],
    subscribe: (cb) => {
      settingsChangeNotifiers.listeners.push(cb);
      return () => {
        settingsChangeNotifiers.listeners =
          settingsChangeNotifiers.listeners.filter((x) => x !== cb);
      };
    },
    notify: (userId, changeType = 'settings') => {
      settingsChangeNotifiers.listeners.forEach((cb) => cb(userId, changeType));
    },
  });

  /* ------------------------------------------------------------------
     ✅ NEW: Force refresh function for external components
  ------------------------------------------------------------------*/
  const refreshData = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  // Keep dashboard consumers synchronized after an individual visibility,
  // TTS, timer, or assignment mutation without re-downloading every setting.
  const patchAssessmentSettingCache = useCallback((settingId, patch) => {
    setAssessmentSettings(previous => previous.map(setting => {
      if (Number(setting.id) !== Number(settingId)) return setting;
      const next = { ...setting, ...patch };
      if (patch.is_visible !== undefined) {
        next.is_visible = isEnabledFlag(patch.is_visible) ? 1 : 0;
      }
      if (patch.tts_enabled !== undefined) {
        next.tts_enabled = isEnabledFlag(patch.tts_enabled) ? 1 : 0;
      }
      if (patch.is_assigned !== undefined) {
        next.is_assigned = isEnabledFlag(patch.is_assigned) ? 1 : 0;
      }
      return next;
    }));
  }, []);

  /* ------------------------------------------------------------------
     ✅ ENHANCED: Memoized fetch functions for better performance
  ------------------------------------------------------------------*/
  const fetchUsersFromServer = useCallback(async () => {
    try {
      
      // ✅ CRITICAL: Use server API instead of local database
      const usersResponse = await api.getAllUsers();
      const freshUsers = Array.isArray(usersResponse) ? usersResponse : [];
           
      // Log sample data to verify fields
      if (freshUsers.length > 0) {
        const sample = freshUsers[0];
      }
      
      // Normalize the user data
      const normUsers = freshUsers.map(normalizeUser);
      
      // ✅ VERIFICATION: Log normalized data
      if (normUsers.length > 0) {
        const sample = normUsers[0];
      }
      
      setAssignedUsers(normUsers);
      return normUsers;
    } catch (error) {
      console.error('❌ [AdminContext] Error fetching users from server:', error);
      return [];
    }
  }, []);

  /* ------------------------------------------------------------------
     ✅ ENHANCED: Fetch performances from server API with assessment_pool
  ------------------------------------------------------------------*/
  const fetchPerformancesFromServer = useCallback(async (currentUser) => {
    try {
      if (!currentUser) {
        console.warn('[AdminContext] No current user - skipping performance fetch');
        return [];
      }

      const isAdmin = currentUser.role === 'admin';
      const accessCode = currentUser.accessCode || currentUser.access_code || '';
      
      const perfFilter = isAdmin || !accessCode ? {} : { codeUsed: accessCode };
      
      const performanceResponse = await api.getPerformances({
        ...perfFilter,
        _cache_bust: Date.now()
      });
      const freshPerformances = Array.isArray(performanceResponse) ? performanceResponse : [];
            
      const complete = freshPerformances.filter((p) => {
        const done =
          p.isComplete === 1 ||
          p.isComplete === true ||
          (typeof p.isComplete === 'string' &&
            p.isComplete.toLowerCase() === 'true');
        return done;
      });
      
      const normPerf = complete.map(normalizePerformance);
      
      setPerformances(normPerf);
      return normPerf;
    } catch (error) {
      console.error('❌ [AdminContext] Error fetching performances from server:', error);
      return [];
    }
  }, []);

  /* ------------------------------------------------------------------
     ✅ NEW: Enhanced assessment settings fetcher for teachers
  ------------------------------------------------------------------*/
  const fetchAssessmentSettingsForTeacher = useCallback(async (accessCode) => {
    try {      
      const settings = await api.getAssessmentSettings({ accessCode });      
      setAssessmentSettings(settings);
      return settings;
    } catch (error) {
      console.error('❌ [AdminContext] Error fetching teacher assessment settings:', error);
      setAssessmentSettings([]);
      return [];
    }
  }, []);

  /* ------------------------------------------------------------------
     ✅ CRITICAL FIX: Optimized initial data load to prevent resource exhaustion
  ------------------------------------------------------------------*/
  useEffect(() => {
    if (!currentUser) return;

    let cancelled = false;
    const loadData = async () => {
      try {
        setIsLoading(true);

        const isAdmin = currentUser.role === 'admin';
        const accessCode = currentUser.accessCode || currentUser.access_code || '';


        /* ---------- Assessments (always) ------------ */
        const stored = await AssessmentModel.findAll();
        const normStored = stored.map((a) => ({
          ...a,
          assessmentPool:
            a.assessmentPool ||
            (a.assessmentKind === 'regular' ? 'NonPooled' : 'NA'),
        }));
        if (!cancelled) {
          setAvailableAssessments(normStored);
        }

        /* ---------- ✅ ENHANCED: Role-specific data loading -------------- */
        if (isAdmin) {
          
          // Existing settings are sparse: a row is created only by explicit
          // assignment. Load users and settings concurrently without creating
          // a user × assessment matrix on every dashboard visit.
          const [, settings] = await Promise.all([
            fetchUsersFromServer(),
            api.getAllAssessmentSettings()
          ]);
          if (cancelled) return;
          if (!cancelled) {
            setAssessmentSettings(settings);
          }
        } else if (['teacher', 'main_user'].includes(currentUser.role) && accessCode) {
          
          // ✅ NEW: Load teacher-specific assessment settings
          await fetchAssessmentSettingsForTeacher(accessCode);
          if (cancelled) return;
        }

        /* ---------- ✅ Performances from server API ------------- */
        if (!cancelled) {
          await fetchPerformancesFromServer(currentUser);
        }


      } catch (e) {
        console.error('❌ [AdminContext] loadData error:', e);
        if (!cancelled) {
          setAssignedUsers([]);
          setPerformances([]);
          setAssessmentSettings([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, [currentUser, refreshKey, fetchPerformancesFromServer, fetchUsersFromServer, fetchAssessmentSettingsForTeacher]);

  /* ------------------------------------------------------------------
     ✅ UPDATED: Enhanced refresh functions
  ------------------------------------------------------------------*/
  const refreshPerformances = useCallback(async () => {
    try {
      return await fetchPerformancesFromServer(currentUser);
    } catch (e) {
      console.error('[AdminContext] refreshPerformances error:', e);
      throw e;
    }
  }, [currentUser, fetchPerformancesFromServer]);

  const refreshUsers = useCallback(async () => {
    try {
      return await fetchUsersFromServer();
    } catch (e) {
      console.error('[AdminContext] refreshUsers error:', e);
      throw e;
    }
  }, [fetchUsersFromServer]);

  /* ------------------------------------------------------------------
     ✅ NEW: Enhanced refresh for teacher assessment settings
  ------------------------------------------------------------------*/
  const refreshTeacherAssessmentSettings = useCallback(async () => {
    if (!currentUser || !['teacher', 'main_user'].includes(currentUser.role)) {
      console.warn('[AdminContext] refreshTeacherAssessmentSettings called for non-teacher');
      return [];
    }
    
    const accessCode = currentUser.accessCode || currentUser.access_code;
    if (!accessCode) {
      console.error('[AdminContext] No access code for teacher');
      return [];
    }
    
    try {
      const settings = await fetchAssessmentSettingsForTeacher(accessCode);
      settingsChangeNotifiers.notify(currentUser.id, 'refresh');
      return settings;
    } catch (e) {
      console.error('[AdminContext] refreshTeacherAssessmentSettings error:', e);
      throw e;
    }
  }, [currentUser, fetchAssessmentSettingsForTeacher, settingsChangeNotifiers]);

  /* ------------------------------------------------------------------
     CRUD / utility functions (updated to use server API)
  ------------------------------------------------------------------*/

  /* ---------------- ✅ ENHANCED: Assign new user using server API ---------------- */
  const assignUser = useCallback(async (userData) => {
  try {         
    
    // ✅ CRITICAL FIX: Use server API for user creation (fast)
    const newUser = await api.createUser(userData);
    
    // ✅ PERFORMANCE OPTIMIZATION: Refresh users list immediately (non-blocking)
    fetchUsersFromServer().catch(console.error);
    
    // ✅ Notify listeners of user creation immediately
    settingsChangeNotifiers.notify(newUser.id, 'user_created');
    
    return newUser;
      
  } catch (e) {
    console.error('❌ [AdminContext] assignUser error:', e);
    
    // ✅ ENHANCED: Better error handling with specific error messages
    let errorMessage = 'Failed to create user';
    
    if (e.message) {
      if (e.message.includes('Email address already exists') || e.message.includes('UNIQUE constraint failed: users.email')) {
        errorMessage = 'Email address already exists';
      } else if (e.message.includes('Access code already exists') || e.message.includes('UNIQUE constraint failed: users.access_code')) {
        errorMessage = 'Access code already exists';
      } else if (e.message.includes('constraint')) {
        errorMessage = 'User with these credentials already exists';
      } else {
        errorMessage = e.message;
      }
    }
    
    // Create a proper Error object to throw
    const enhancedError = new Error(errorMessage);
    enhancedError.originalError = e;
    
    throw enhancedError;
  }
}, [fetchUsersFromServer, settingsChangeNotifiers]);

  /* ---------------- ✅ ENHANCED: Delete user using server API ---------------- */
  const deleteUser = useCallback(async (userId) => {
    try {
      
      // ✅ Use server API instead of local database
      await api.deleteUser(userId);
      
      // ✅ Immediately refresh users from server
      await fetchUsersFromServer();
      
      // Notify listeners of user deletion
      settingsChangeNotifiers.notify(userId, 'user_deleted');
    
    } catch (e) {
      console.error('[AdminContext] deleteUser error:', e);
      throw e;
    }
  }, [fetchUsersFromServer, settingsChangeNotifiers]);

  /* ---------------- ✅ ENHANCED: Update user status using server API ---------------- */
  const updateUserStatus = useCallback(async (userId, isEnabled) => {
    try {      
      // ✅ Use server API instead of local database
      await api.patchUser(userId, { isEnabled });
      
      // ✅ Immediately refresh users from server
      await fetchUsersFromServer();
      
      // Notify listeners of status change
      settingsChangeNotifiers.notify(userId, 'status_updated');
    } catch (e) {
      console.error('[AdminContext] updateUserStatus error:', e);
      throw e;
    }
  }, [fetchUsersFromServer, settingsChangeNotifiers]);

  /* ---------------- ✅ ENHANCED: Update user validity using server API ---------------- */
  const updateUserValidity = useCallback(async (userId, validityDays) => {
    try {      
      // ✅ Use server API instead of local database
      await api.patchUser(userId, { validityDays });
      
      // ✅ Immediately refresh users from server
      await fetchUsersFromServer();
      
      // Notify listeners of validity change
      settingsChangeNotifiers.notify(userId, 'validity_updated');
      
    } catch (e) {
      console.error('[AdminContext] updateUserValidity error:', e);
      throw e;
    }
  }, [fetchUsersFromServer, settingsChangeNotifiers]);

  /* ---------------- ✅ FIXED: Add assessment with simplified teacher visibility ---------------- */
  const addAssessment = useCallback(async (data) => {
  try {      
    
    const withPool = {
      id: data.id,
      name: data.name,
      fileName: data.fileName,
      assessmentKind: data.assessmentKind || 'regular',
      assessmentPool:
        data.assessmentKind === 'regular'
          ? data.assessmentPool || 'NonPooled'
          : 'NA',
      ...(Object.prototype.hasOwnProperty.call(data, 'extractToItemBank')
        ? { extractToItemBank: Boolean(data.extractToItemBank) }
        : {}),
      isNew: data.isNew === true, // Ensure it's a boolean
      upload_source: currentUser?.role || 'unknown',
      upload_timestamp: new Date().toISOString()
    };
    
    const result = await api.createAssessment(withPool);
    
    // Ensure the isNew flag is preserved in the result
    const assessmentWithFlag = {
      ...result,
      isNew: data.isNew === true
    };
    
    // Update local state with the new assessment
    setAvailableAssessments((prev) => {
      const updated = [...prev, assessmentWithFlag];
      return updated;
    });
    
    // Settings are intentionally sparse. Creating an assessment never assigns
    // it to every account; the assignment modal writes only the changed main
    // and sub-user rows in one bulk transaction.
    
    return assessmentWithFlag;
  } catch (e) {
    console.error('[AdminContext] addAssessment error:', e);
    throw e;
  }
}, [currentUser]);

  /* ---------------- Update assessment ---------------- */
  const updateAssessment = useCallback(async (id, data) => {
    try {      
      const upd = { ...data };
      if (upd.assessmentKind === 'regular') {
        if (!upd.assessmentPool) upd.assessmentPool = 'NonPooled';
      } else if (upd.assessmentKind === 'gradeLevel') {
        upd.assessmentPool = 'NA';
      }
      
      const updated = await api.updateAssessment(id, upd);
      setAvailableAssessments((p) =>
        p.map((a) => (a.id === id ? updated : a))
      );
    
      return updated;
    } catch (e) {
      console.error('[AdminContext] updateAssessment error:', e);
      throw e;
    }
  }, []);

  /* ---------------- ✅ FIXED: Delete assessment with role-based settings cleanup ---------------- */
  const deleteAssessment = useCallback(async (assessmentId) => {
    try {      
      // The server removes catalog settings in the same transaction as the
      // assessment. Avoid a second full settings download and redundant
      // per-row deletes here.
      await AssessmentModel.delete(assessmentId);
      setAvailableAssessments((p) => p.filter((a) => a.id !== assessmentId));
      setAssessmentSettings((rows) => rows.filter(row => !sameAssessment(row, assessmentId)));
    } catch (e) {
      console.error('[AdminContext] deleteAssessment error:', e);
      throw e;
    }
  }, []);

  /* ---------------- Toggle assessment visibility ---------------- */
  const updateAssessmentVisibility = useCallback(async (assessmentId, isVisible) => {
    try {      
      const rows = await AssessmentSettingsModel.findByAssessmentType(
        assessmentId
      );
      for (const row of rows) {
        await AssessmentSettingsModel.update(row.id, {
          is_visible: isVisible,
          expectedRevision: Number(row.row_revision || 1)
        });
      }
      const upd = await AssessmentSettingsModel.findAll();
      setAssessmentSettings(upd);
      
      // Notify all users that visibility changed
      rows.forEach(row => {
        settingsChangeNotifiers.notify(row.user_id, 'visibility_updated');
      });
      
    } catch (e) {
      console.error('[AdminContext] updateAssessmentVisibility error:', e);
      throw e;
    }
  }, [settingsChangeNotifiers]);

  /* ---------------- ✅ SIMPLIFIED: Restricted teacher assessment visibility (Teacher + Admin only) ---------------- */
  const getAvailableAssessments = useCallback(async (accessCode, userRole = null) => {
    try {
      if (userRole === 'admin') return availableAssessments;
      if (userRole === 'teacher' || userRole === 'main_user') {
        return availableAssessments.filter(assessment =>
          !isUserOwnedAssessment(assessment) || assessmentIsOwnedBy(assessment, currentUser)
        );
      }
      if (!accessCode) return [];
      // Student/sub-user access must fail closed and comes from the server's
      // exact assessment-ID + assigned + visible join.
      return await api.getAvailableAssessments(accessCode);
    } catch (e) {
      console.error('[AdminContext] getAvailableAssessments error:', e);
      throw e;
    }
  }, [availableAssessments, currentUser]);

  /* ---------------- ✅ NEW: Refresh teacher assessments from server ---------------- */
  const refreshTeacherAssessmentsFromServer = useCallback(async () => {
    try {
      const allAssessments = await api.getAllAssessments();
      const teacherAssessments = allAssessments.filter(assessment =>
        assessmentIsOwnedBy(assessment, currentUser)
      );
      
      
      // Update the global availableAssessments state with fresh data
      setAvailableAssessments(prev => {
        // Remove old assessments from this teacher and add fresh ones
        const withoutTeacherAssessments = prev.filter(a => !assessmentIsOwnedBy(a, currentUser));
        const updated = [...withoutTeacherAssessments, ...teacherAssessments];
        return updated;
      });
      
      return teacherAssessments;
    } catch (error) {
      console.error('[AdminContext] refreshTeacherAssessmentsFromServer error:', error);
      throw error;
    }
  }, [currentUser]);

  /* ---------------- ✅ FIXED: Get assessments for teacher management (without circular dependency) ---------------- */
  const getTeacherAssessments = useCallback(async () => {
    try {
      const teacherAssessments = availableAssessments.filter(
        assessment => assessmentIsOwnedBy(assessment, currentUser) ||
          assessment?.source_type === 'bank_template'
      );
      
      
      return teacherAssessments;
    } catch (e) {
      console.error('[AdminContext] getTeacherAssessments error:', e);
      return [];
    }
  }, [availableAssessments, currentUser]);

  /* ---------------- ✅ NEW: Get all assessments for admin (including teacher-uploaded ones) ---------------- */
  const getAllAssessmentsForAdmin = useCallback(async () => {
    try {
      if (!currentUser || currentUser.role !== 'admin') {
        console.warn('[AdminContext] getAllAssessmentsForAdmin called by non-admin user');
        return [];
      }
      
      // Admin can see ALL assessments (system + teacher-uploaded)
      return availableAssessments;
    } catch (e) {
      console.error('[AdminContext] getAllAssessmentsForAdmin error:', e);
      throw e;
    }
  }, [availableAssessments, currentUser]);

  /* ---------------- ✅ NEW: Check if user can access specific assessment ---------------- */
  const canUserAccessAssessment = useCallback((assessment, userAccessCode, userRole) => {
    // Admin can access everything
    if (userRole === 'admin') {
      return true;
    }
    
    if ((userRole === 'teacher' || userRole === 'main_user') && assessmentIsOwnedBy(assessment, currentUser)) {
      return true;
    }

    const setting = assessmentSettings.find(
      s => sameAssessment(s, assessment) && s.access_code === userAccessCode
    );

    return Boolean(setting && settingIsAssigned(setting) && isEnabledFlag(setting.is_visible));
  }, [assessmentSettings, currentUser]);

  /* ---------------- ✅ ENHANCED: Get assessments based on user role and access ---------------- */
  const getAssessmentsForUser = useCallback(async (accessCode, userRole) => {
    try {
      let assessments = [];
      
      if (userRole === 'admin') {
        // Admin sees everything
        assessments = await getAllAssessmentsForAdmin();
      } else if (userRole === 'teacher' || userRole === 'main_user') {
        assessments = await getAvailableAssessments(accessCode, userRole);
      } else {
        assessments = await api.getAvailableAssessments(accessCode);
      }
      
      return assessments;
    } catch (e) {
      console.error('[AdminContext] getAssessmentsForUser error:', e);
      throw e;
    }
  }, [getAllAssessmentsForAdmin, getAvailableAssessments]);

  /* ---------------- recordPerformance with event-driven refresh ---------------- */
  const recordPerformance = useCallback(async (data) => {
    try {      
      const res = await PerformanceModel.create(data);
      const newPerfRaw = {
        ...data,
        id: res.lastID,
        test_date: new Date().toISOString().split('T')[0],
        test_time: new Date().toLocaleTimeString('en-US', { hour12: false }),
      };
      const newPerf = normalizePerformance(newPerfRaw);
      setPerformances((prev) => {
        const upd = [newPerf, ...prev];
        return upd.sort((a, b) => {
          const dA = new Date(`${a.testDate}T${a.testTime}`);
          const dB = new Date(`${b.testDate}T${b.testTime}`);
          return dB - dA;
        });
      });

      // ✅ Enhanced: Refresh performances after a delay
      setTimeout(() => {
        refreshPerformances().catch(console.error);
      }, 1000);

      return newPerf;
    } catch (e) {
      console.error('[AdminContext] recordPerformance error:', e);
      throw e;
    }
  }, [refreshPerformances]);

  /* ---------------- Get user by email & pin ---------------- */
  const getAssignedUser = useCallback(async (email, pin) => {
    try {
      const u = await UserModel.findByEmailAndPin(email, pin);
      return u ? normalizeUser(u) : null;
    } catch (e) {
      console.error('[AdminContext] getAssignedUser error:', e);
      throw e;
    }
  }, []);

  /* ---------------- Stats ---------------- */
  const getPerformanceStats = useCallback(async () => {
    try {
      const uniqueStudents = new Set(
        performances
          .map((p) =>
            p.studentName && p.actualGradeLevel
              ? `${p.studentName.trim().toLowerCase()}_${p.actualGradeLevel
                  .trim()
                  .toLowerCase()}`
              : null
          )
          .filter(Boolean)
      );
      const byGrade = new Map();
      performances.forEach((p) => {
        if (p.studentName && p.actualGradeLevel) {
          const grade = p.actualGradeLevel.trim();
          const key = `${p.studentName.trim().toLowerCase()}_${grade.toLowerCase()}`;
          if (!byGrade.has(grade)) byGrade.set(grade, new Set());
          byGrade.get(grade).add(key);
        }
      });
      const gradeBreakdown = {};
      byGrade.forEach((set, g) => (gradeBreakdown[g] = set.size));
      return {
        totalPerformances: performances.length,
        uniqueStudents: uniqueStudents.size,
        gradeBreakdown,
      };
    } catch (e) {
      console.error('[AdminContext] getPerformanceStats error:', e);
      return {
        totalPerformances: performances.length,
        uniqueStudents: 0,
        gradeBreakdown: {},
      };
    }
  }, [performances]);

  /* ---------------- removePerformance (client only) ---------------- */
  const removePerformance = useCallback((id) => {
    setPerformances((p) => p.filter((perf) => perf.id !== id));
  }, []);

  /* ---------------- ✅ FIXED: Simplified forceRefreshUserSettings - no longer searches for missing users ---------------- */
  const forceRefreshUserSettings = useCallback(async (userId, ignoreCache = true) => {
    try {      
      const ts = Date.now();
      
      // ✅ SIMPLIFIED: Only work with users that exist in assignedUsers
      const user = assignedUsers.find((u) => u.id === userId);
      
      if (!user) {
        // ✅ SIMPLIFIED: Just log a debug message and return early
        console.debug(
          '[AdminContext] forceRefreshUserSettings: Skipping refresh for user not in assignedUsers:',
          userId,
          '(This is normal for sub-users managed independently)'
        );
        return [];
      }
      
      // ✅ Use the API client for consistency
      const fresh = await api.getAssessmentSettings({ 
        accessCode: user.accessCode,
        _cache_bust: ignoreCache ? ts : undefined
      });
      
      setAssessmentSettings((prev) =>
        prev.map((s) => {
          const f = fresh.find(
            (fs) =>
              sameAssessment(fs, s) &&
              Number(fs.user_id) === Number(userId)
          );
          return f
            ? {
                ...s,
                is_visible: f.is_visible,
                tts_enabled: f.tts_enabled,
              }
            : s;
        })
      );
      
      settingsChangeNotifiers.notify(userId, 'force_refresh');
      return fresh;
    } catch (e) {
      console.debug('[AdminContext] forceRefreshUserSettings error (non-critical):', e);
      return [];
    }
  }, [assignedUsers, settingsChangeNotifiers]);

  /* ---------------- refreshAssessments & others ---------------- */
  const refreshAssessments = useCallback(async () => {
    try {      
      const stored = await AssessmentModel.findAll();
      const normStored = stored.map((a) => ({
        ...a,
        assessmentPool:
          a.assessmentPool ||
          (a.assessmentKind === 'regular' ? 'NonPooled' : 'NA'),
      }));
      setAvailableAssessments(normStored);
      
      return normStored;
    } catch (e) {
      console.error('[AdminContext] refreshAssessments error:', e);
      throw e;
    }
  }, []);

  const refreshStudentResponses = useCallback(async () => true, []);
  const refreshSessions = useCallback(async () => true, []);

  /* ---------------- auth helpers ---------------- */
  const loginUser = useCallback((u) => {
    setCurrentUser(u);
  }, []);
  
  const logoutUser = useCallback(() => {
    setCurrentUser(null);
    setAssignedUsers([]);
    setPerformances([]);
    setAssessmentSettings([]);
  }, []);

  /* ------------------------------------------------------------------
     ✅ ENHANCED: Memoised context value with teacher-specific functions
  ------------------------------------------------------------------*/
  const value = useMemo(
    () => ({
      /* data */
      assignedUsers,
      performances,
      availableAssessments,
      assessmentSettings,
      currentUser,
      isLoading,
      isCreatingUser,
      /* auth */
      loginUser,
      logoutUser,
      /* CRUD & utils */
      assignUser,
      deleteUser,
      recordPerformance,
      removePerformance,
      getAssignedUser,
      getPerformanceStats,
      refreshUsers,
      updateUserStatus,
      updateUserValidity,
      forceRefreshUserSettings,
      patchAssessmentSettingCache,
      /* assessments */
      addAssessment,
      updateAssessment,
      deleteAssessment,
      updateAssessmentVisibility,
      refreshAssessments,
      /* ✅ ENHANCED: Teacher-specific functions with restricted visibility */
      refreshTeacherAssessmentSettings,
      getTeacherAssessments,           // Only teacher's own assessments
      refreshTeacherAssessmentsFromServer, // ✅ NEW: Server refresh function
      getAllAssessmentsForAdmin,       // All assessments (admin only)
      canUserAccessAssessment,         // Check access permissions
      getAssessmentsForUser,           // Role-based assessment access
      /* ✅ LEGACY: Keep for backward compatibility */
      getAvailableAssessments,         // Now includes role-based filtering
      refreshStudentResponses,
      refreshSessions,
      refreshPerformances,
      refreshData,
      settingsChangeNotifier: settingsChangeNotifiers,
    }),
    [
      assignedUsers,
      performances,
      availableAssessments,
      assessmentSettings,
      currentUser,
      isLoading,
      loginUser,
      logoutUser,
      assignUser,
      deleteUser,
      recordPerformance,
      removePerformance,
      getAssignedUser,
      getPerformanceStats,
      refreshUsers,
      updateUserStatus,
      updateUserValidity,
      forceRefreshUserSettings,
      patchAssessmentSettingCache,
      addAssessment,
      updateAssessment,
      deleteAssessment,
      updateAssessmentVisibility,
      refreshAssessments,
      refreshTeacherAssessmentSettings,
      getTeacherAssessments,
      refreshTeacherAssessmentsFromServer, // ✅ NEW: Added dependency
      getAllAssessmentsForAdmin,
      canUserAccessAssessment,
      getAssessmentsForUser,
      getAvailableAssessments,
      refreshStudentResponses,
      refreshSessions,
      refreshPerformances,
      refreshData,
      settingsChangeNotifiers,
      isCreatingUser, 
    ]
  );

  /* ------------------------------------------------------------------
     Loading overlay while fetching initial admin data
  ------------------------------------------------------------------*/
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          backgroundColor: dashboardPaint('#1a202c', 'background', false),
        }}
      >
        <style>{`
          @keyframes spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }
        `}</style>
        <div
          style={{
            border: dashboardPaint('8px solid #f3f3f3', 'border', false),
            borderTop: dashboardPaint('8px solid #2563eb', 'border', false),
            borderRadius: '50%',
            width: 60,
            height: 60,
            animation: 'spin 1s linear infinite',
          }}
        />
        <h2
          style={{
            marginTop: 20,
            color: dashboardPaint('#fff', 'text', false),
            fontSize: 18,
            fontWeight: 'bold',
          }}
        >
          Loading admin data…
        </h2>
      </div>
    );
  }

  /* ------------------------------------------------------------------
     Provider render
  ------------------------------------------------------------------*/
  return (
    <AdminContext.Provider value={value}>{children}</AdminContext.Provider>
  );
}

/* ──────────────────────────────────────────────────────────────────
   Hook
─────────────────────────────────────────────────────────────────── */
export function useAdmin() {
  const ctx = useContext(AdminContext);
  if (!ctx)
    throw new Error('useAdmin must be called inside an <AdminProvider>');
  return ctx;
}
